// pages/tasks/dataAnalystTat.js
//
// Data Analyst Own Ticket TAT rules (phase 66), shared by OwnTicketModal,
// EditOwnTicketModal and the list/detail pages. Mirrors the backend
// (services/dataAnalystTatService.js), which is the authority: for
// High/Medium/Low it derives the TAT itself and ignores anything sent.
//   High            -> 4 Hours
//   Medium          -> 2 Days
//   Low             -> 5 Days
//   New Requirement -> custom value + Hours/Days

export const NEW_REQUIREMENT = "new_requirement";

export const FIXED_TAT = Object.freeze({
  high: { value: 4, unit: "hours" },
  medium: { value: 2, unit: "days" },
  low: { value: 5, unit: "days" },
});

export const TAT_UNIT_OPTIONS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

export const TAT_LEVEL_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: NEW_REQUIREMENT, label: "New Requirement" },
];

export const isFixedLevel = (level) => Object.prototype.hasOwnProperty.call(FIXED_TAT, level);

export const levelLabel = (level) =>
  TAT_LEVEL_OPTIONS.find((o) => o.value === String(level || "").toLowerCase())?.label || level || "—";

// "4 Days" / "1 Hour"
export function formatTat(value, unit) {
  if (value === null || value === undefined || value === "" || !unit) return "—";
  const label = TAT_UNIT_OPTIONS.find((o) => o.value === String(unit).toLowerCase())?.label || unit;
  return Number(value) === 1 ? `${value} ${String(label).replace(/s$/, "")}` : `${value} ${label}`;
}

// Hint shown next to each level choice.
export const levelHint = (level) => (isFixedLevel(level) ? formatTat(FIXED_TAT[level].value, FIXED_TAT[level].unit) : "Custom TAT");

/**
 * The TAT a selection resolves to, or null while incomplete/invalid.
 * @returns {{ level, value, unit } | null}
 */
export function appliedTat({ level, value, unit }) {
  if (isFixedLevel(level)) return { level, ...FIXED_TAT[level] };
  if (level !== NEW_REQUIREMENT) return null;
  const str = String(value ?? "").trim();
  if (!/^\d+$/.test(str) || Number(str) <= 0) return null;
  if (!TAT_UNIT_OPTIONS.some((o) => o.value === unit)) return null;
  return { level, value: Number(str), unit };
}

// Same rules as the backend's deriveTat(); "" = OK.
export function tatErrors({ level, value, unit }) {
  const errors = { level: "", value: "", unit: "" };
  if (!TAT_LEVEL_OPTIONS.some((o) => o.value === level)) {
    errors.level = "Select a TAT level.";
    return errors;
  }
  if (level === NEW_REQUIREMENT) {
    const str = String(value ?? "").trim();
    if (!/^\d+$/.test(str) || Number(str) <= 0) errors.value = "Enter a positive whole number, e.g. 7.";
    if (!TAT_UNIT_OPTIONS.some((o) => o.value === unit)) errors.unit = "Select Hours or Days.";
  }
  return errors;
}

// Request keys for a selection: High/Medium/Low send ONLY the level (the
// server derives value/unit); New Requirement sends value + unit too.
export function tatPayload({ level, value, unit }) {
  if (level === NEW_REQUIREMENT) {
    return {
      data_analyst_tat_level: level,
      data_analyst_tat_value: Number(String(value).trim()),
      data_analyst_tat_unit: unit,
    };
  }
  return { data_analyst_tat_level: level };
}

// "Jajmau, Deoria +2" -- compact list cell for multi-value selections.
export function summarizeNames(items, visible = 2) {
  const names = (items || []).map((i) => (typeof i === "string" ? i : i?.name)).filter(Boolean);
  if (!names.length) return "";
  const shown = names.slice(0, visible).join(", ");
  return names.length > visible ? `${shown} +${names.length - visible}` : shown;
}
